/* eslint-disable indent */
/* eslint-disable max-len */
/* eslint-disable react/prop-types */

import {
  InfoOutlined,
  Task,
  Today,
} from '@mui/icons-material';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import CalendarViewMonth from '@mui/icons-material/CalendarViewMonth';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import {
  DateCalendar,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  setPlannerCalendarView,
  setPlannerGridView,
} from '../../../../redux/planner/actions';
import theme from '../../../../utils/theme';

import BarnIcon from '../../../CustomIcons/Barn';

import BarnHorsesAutocomplete from '../../../Autocompletes/BarnHorses';
import BarnUsersAutocomplete from '../../../Autocompletes/BarnUsers';
import TagsAutocomplete from '../../../Autocompletes/Tags';
import TaskDefinitionAutocomplete from '../../../Autocompletes/TaskDefinition';

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'dayIsBetween'
    && prop !== 'isFirstDay'
    && prop !== 'isLastDay'
    && prop !== 'isToday',
})(({
  theme: muiTheme,
  dayIsBetween,
  isFirstDay,
  isLastDay,
  isToday,
}) => ({
  borderRadius: 0,
  ...(dayIsBetween && {
    backgroundColor: muiTheme.palette.secondary.main,
    color: muiTheme.palette.secondary.contrastText,
    border: 0,
    '&:hover, &:focus': { backgroundColor: muiTheme.palette.primary.light },
  }),
  ...(isFirstDay && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    backgroundColor: muiTheme.palette.primary.main,
  }),
  ...(isLastDay && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
    backgroundColor: muiTheme.palette.primary.main,
  }),
  ...(isToday && {
    outline: `2px solid ${muiTheme.palette.primary.main}`,
    backgroundColor: muiTheme.palette.primary.main,
    color: muiTheme.palette.primary.contrastText,
    outlineOffset: 1,
  }),
}));

const WeekPickerDay = ({
  day,
  outsideCurrentMonth,
  startOfWeek,
  endOfWeek,
  ...other
}) => {
  if (outsideCurrentMonth) {
    return <PickersDay day={day} outsideCurrentMonth {...other} />;
  }

  const dayMoment = day && typeof day.isSame === 'function'
    ? day
    : moment(day);
  const startMoment = startOfWeek
    ? moment(startOfWeek).startOf('day')
    : null;
  const endMoment = endOfWeek
    ? moment(endOfWeek).endOf('day')
    : null;

  const isFirstDay = startMoment
    ? dayMoment.isSame(startMoment, 'day')
    : false;
  const isLastDay = endMoment
    ? dayMoment.isSame(endMoment, 'day')
    : false;
  const dayIsBetween = startMoment && endMoment
    ? dayMoment.isBetween(startMoment, endMoment, 'day', '[]')
    : false;
  const isToday = dayMoment.isSame(moment(), 'day');

  return (
    <CustomPickersDay
      {...other}
      day={dayMoment}
      outsideCurrentMonth={outsideCurrentMonth}
      dayIsBetween={dayIsBetween}
      isFirstDay={isFirstDay}
      isLastDay={isLastDay}
      isToday={isToday}
    />
  );
};

WeekPickerDay.propTypes = {
  day: PropTypes.any.isRequired,
  outsideCurrentMonth: PropTypes.bool,
  startOfWeek: PropTypes.instanceOf(Date),
  endOfWeek: PropTypes.instanceOf(Date),
};

WeekPickerDay.defaultProps = {
  outsideCurrentMonth: false,
  startOfWeek: null,
  endOfWeek: null,
};

const FilterBarComponent = ({
  selectedScheduleUser,
  setSelectedScheduleUser,
  selectedParticipantUser,
  setSelectedParticipantUser,
  setIncludeCalendarEvents,
  setIncludeHorseTasks,
  setIncludeBarnTasks,
  includeCalendarEvents,
  includeHorseTasks,
  includeBarnTasks,
  setSelectedBarnHorses,
  defaultBarnHorses,
  selectedBarnHorses,
  setTags,
  tags,
  setCurrentView,
  currentView,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  eventTemplatesFilter,
  setEventTemplatesFilter,
  selectedCalendarId,
  setSelectedCalendarId,
}) => {
  // eslint-disable-next-line no-unused-vars
  const smallDown = useMediaQuery(theme.breakpoints.down('sm'));
  const mediumDown = useMediaQuery(theme.breakpoints.down('xl'));
  const dispatch = useDispatch();

  // Redux State
  const boardLocation = useSelector((state) => state.board.location);
  const allEventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const weekStyle = useSelector((state) => state.planner.weekStyle || 'isoWeek');
  const gridView = useSelector((state) => state.planner.gridView || 'week');
  const calendarView = useSelector((state) => state.planner.calendarView || 'week');

  React.useEffect(() => {
    if (selectedBarnHorses && selectedBarnHorses.length > 0 && !defaultBarnHorses) {
      // If the board location changes, reset any filter horses
      setSelectedBarnHorses([])
    }
  }, [boardLocation])

  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: {
          xs: 'calc(100vh - 120px)',
          md: 'calc(100vh - 160px)',
        },
        overflowY: 'auto',
        pr: 1,
        width: '100%',
        maxWidth: 300,
      }}
    >
      <Grid container spacing={2}>
        <Grid
          size={12}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 2,
              flexGrow: 1,
            }}
          >
            <ToggleButtonGroup
              color="primary"
              value={currentView}
              exclusive
              onChange={(e, newView) => {
                if (!newView) {
                  return;
                }
                const referenceMoment = startDate
                  ? moment(startDate)
                  : moment();
                const calendarReferenceMoment = referenceMoment;
                if (newView === 'grid') {
                  const gridReferenceMoment = referenceMoment;
                  if (calendarView === 'day' || calendarView === 'agenda') {
                    dispatch(setPlannerGridView('day'));
                    setStartDate(gridReferenceMoment.clone().startOf('day').toDate());
                    setEndDate(gridReferenceMoment.clone().endOf('day').toDate());
                  } else {
                    dispatch(setPlannerGridView('week'));
                    setStartDate(gridReferenceMoment.clone().startOf(weekStyle).startOf('day').toDate());
                    setEndDate(gridReferenceMoment.clone().endOf(weekStyle).endOf('day').toDate());
                  }
                } else if (gridView === 'day') {
                  dispatch(setPlannerCalendarView('day'));
                  setStartDate(calendarReferenceMoment.clone().startOf('day').toDate());
                  setEndDate(calendarReferenceMoment.clone().endOf('day').toDate());
                } else {
                  dispatch(setPlannerCalendarView('week'));
                  setStartDate(calendarReferenceMoment.clone().startOf('week').startOf('day').toDate());
                  setEndDate(calendarReferenceMoment.clone().endOf('week').endOf('day').toDate());
                }
                setCurrentView(newView);
              }}
              aria-label="Task and Event View"
              sx={{ width: '100%' }}
            >
              <ToggleButton
                size="small"
                value="grid"
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Tooltip title="Planning Grid View">
                  <CalendarViewMonth
                    sx={[mediumDown
                      ? { mr: 0 }
                      : { mr: 1 }]}
                  />
                </Tooltip>
                <Box sx={{ width: 75 }}>Planning</Box>
              </ToggleButton>
              <ToggleButton
                size="small"
                value="calendar"
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Tooltip title="Calendar View">
                  <CalendarMonth
                    sx={[mediumDown
                      ? { mr: 0 }
                      : { mr: 1 }]}
                  />
                </Tooltip>
                <Box sx={{ width: 75 }}>Calendar</Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateCalendar
              // value={moment(startDate)}
              onChange={(newValue) => {
                const selectedDayMoment = moment(newValue).startOf('day');
                if (currentView === 'grid') {
                  dispatch(setPlannerGridView('day'));
                }
                const numDays = moment(endDate).diff(moment(startDate), 'days');
                if (numDays <= 1) {
                  // We are in single day view so keep single day view
                  setStartDate(selectedDayMoment.clone().toDate());
                  setEndDate(selectedDayMoment.clone().endOf('day').toDate());
                } else if (numDays <= 7) {
                  // We are in week view so keep week view
                  setStartDate(selectedDayMoment.clone().startOf('week').toDate());
                  setEndDate(selectedDayMoment.clone().endOf('week').toDate());
                } else {
                  // In month view so keep month
                  setStartDate(selectedDayMoment.clone().startOf('month').toDate());
                  setEndDate(selectedDayMoment.clone().endOf('month').toDate());
                }
              }}
              sx={{
                width: '100% !important', // Allow it to fill the parent's width
                margin: '0  !important',
                '& .MuiDateCalendar-root': {
                  width: '100% !important', // Allow it to fill the parent's width
                  margin: '0  !important',
                },
              }}
              slots={{ day: WeekPickerDay }}
            // slotProps={{
            //   day: {
            //     startOfWeek: startDate,
            //     endOfWeek: endDate,
            //   },
            // }}
            />
          </LocalizationProvider>
          <Divider />
        </Grid>
        <Grid size={12}>
          <FormGroup>
            <FormControlLabel
              sx={{
                m: 0,
                mr: 1.5,
              }}
              labelPlacement="end"
              control={<Checkbox checked={includeHorseTasks} onChange={(e) => setIncludeHorseTasks(e.target.checked)} />}
              label={(
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  <Task />
                  <Typography>Horse Tasks
                  </Typography>
                </Box>
              )}
            />
            <FormControlLabel
              sx={{ m: 0 }}
              labelPlacement="end"
              control={<Checkbox checked={includeBarnTasks} onChange={(e) => setIncludeBarnTasks(e.target.checked)} />}
              label={(
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  <BarnIcon />
                  <Typography>Barn Tasks
                  </Typography>
                </Box>
              )}
            />
            <FormControlLabel
              sx={{
                m: 0,
                mr: 1.5,
              }}
              labelPlacement="end"
              control={<Checkbox checked={includeCalendarEvents} onChange={(e) => setIncludeCalendarEvents(e.target.checked)} />}
              label={(
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  <Today />
                  <Typography>Calendar Events
                  </Typography>
                </Box>
              )}
            />
          </FormGroup>
        </Grid>
        <Grid size={12}>
          <TaskDefinitionAutocomplete
            label="Task(s)"
            size="small"
            multiple
            value={eventTemplatesFilter.filter((t) => t !== selectedCalendarId).map((id) => (allEventTemplates.find((et) => et.id === id) || {
              id,
              name: 'Unknown',
            }))}
            eventTemplates={null} // needs to be null so component uses all templates
            onSelect={(e, taskDefinitions) => {
              setEventTemplatesFilter(taskDefinitions.map((et) => et.id));
              setSelectedCalendarId(null)
            }}
            includeEventTaskTypeTagId={false}
            limitTags={2}
            fullWidth
            icon
          />
        </Grid>
        <Grid size={12}>
          <TaskDefinitionAutocomplete
            label="Calendar"
            id="planner-filter-calendar-autocomplete"
            size="small"
            multiple={false}
            value={selectedCalendarId && allEventTemplates.find((et) => et.id === selectedCalendarId)}
            eventTemplates={null} // needs to be null so component uses all templates
            onSelect={(e, taskDefinition) => {
              const newCalendarId = taskDefinition && taskDefinition.id;
              setSelectedCalendarId(newCalendarId);
              setEventTemplatesFilter(!newCalendarId
                ? []
                : [newCalendarId, ...((taskDefinition.children && taskDefinition.children.map((t) => t.id)) || [])])
            }}
            includeEventTaskTypeTagId
            includeHorseTaskTypeTagId={false}
            includeBarnTaskTypeTagId={false}
            fullWidth
            icon
          />
        </Grid>
        <Grid size={12}>
          <BarnHorsesAutocomplete
            multiple
            value={selectedBarnHorses}
            onSelect={(e, newHorses) => { setSelectedBarnHorses(newHorses) }}
            limitTags={1}
            barnHorses={undefined}
            size="small"
            label="Filter by horse"
          />
        </Grid>
        <Grid size={12}>
          <TagsAutocomplete
            onSelect={(e, value) => { setTags(value) }}
            value={tags}
            size="small"
            editMode={false}
            limitTags={1}
            label="Filter by tag"
          />
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <BarnUsersAutocomplete
              label="Assigned User"
              multiple={false}
              value={selectedScheduleUser}
              onSelect={(e, newBarnUser) => setSelectedScheduleUser(newBarnUser)}
              limitTags={1}
            />
            <Tooltip title="When a user is selected here, this user will be placed as the assigned user for tasks you schedule on this page.">
              <InfoOutlined
                color="secondary"
                sx={{
                  fontSize: 22,
                  ml: 0.5,
                }}
              />
            </Tooltip>
          </Box>
        </Grid>
        <Grid size={12}>
          <BarnUsersAutocomplete
            label="Participant"
            multiple={false}
            value={selectedParticipantUser}
            onSelect={(e, newBarnUser) => setSelectedParticipantUser(newBarnUser)}
            limitTags={1}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

FilterBarComponent.propTypes = {
  selectedScheduleUser: PropTypes.any,
  setSelectedScheduleUser: PropTypes.func.isRequired,
  selectedParticipantUser: PropTypes.any,
  setSelectedParticipantUser: PropTypes.func.isRequired,
  setIncludeCalendarEvents: PropTypes.func.isRequired,
  setIncludeHorseTasks: PropTypes.func.isRequired,
  setIncludeBarnTasks: PropTypes.func.isRequired,
  includeCalendarEvents: PropTypes.bool.isRequired,
  includeHorseTasks: PropTypes.bool.isRequired,
  includeBarnTasks: PropTypes.bool.isRequired,
  setSelectedBarnHorses: PropTypes.func.isRequired,
  selectedBarnHorses: PropTypes.array.isRequired,
  defaultBarnHorses: PropTypes.array,
  setTags: PropTypes.func.isRequired,
  tags: PropTypes.array.isRequired,
  setCurrentView: PropTypes.func.isRequired,
  currentView: PropTypes.string.isRequired,
  startDate: PropTypes.any.isRequired,
  setStartDate: PropTypes.func.isRequired,
  endDate: PropTypes.any.isRequired,
  setEndDate: PropTypes.func.isRequired,
  eventTemplatesFilter: PropTypes.any.isRequired,
  setEventTemplatesFilter: PropTypes.func.isRequired,
  selectedCalendarId: PropTypes.any,
  setSelectedCalendarId: PropTypes.func.isRequired,
};

FilterBarComponent.defaultProps = {
  selectedScheduleUser: null,
  selectedParticipantUser: null,
  defaultBarnHorses: null,
  selectedCalendarId: null,
};

export default (FilterBarComponent);
